import type {
  EntityId,
  MatchConnection,
  MatchState,
  PlayerId,
  Unsubscribe,
} from '@signalclash/shared';

// Centralised kill-streak / first-blood detector. Subscribes to MatchConnection
// snapshots, diffs squads to detect deaths, attributes enemy deaths to the
// viewer, and emits high-level events: 'first_blood', 'kill_streak'.
//
// Sfx.ts has its own internal streak counter for audio cues — keeping that
// independent so audio scheduling stays self-contained. This module is the
// VISUAL feedback channel: banners + kill feed read from here. Both modules
// observe the same snapshot stream so their detection stays in lock-step
// without explicit coordination.
//
// Pure presentation (Rule 1): no writes back to MatchState.

export type KillStreakTier = 'single' | 'double' | 'triple' | 'rampage' | 'godlike';

export interface KillEvent {
  // Owner of the squad that died. Used by the kill feed to colour the row.
  victimOwnerId: PlayerId;
  // Attributed killer slot. The simulation doesn't track per-projectile
  // attribution, so this is "the opposing player" — sufficient for a 1v1.
  killerOwnerId: PlayerId;
  // The dying squad's entity id (for de-dup downstream).
  victimSquadId: EntityId;
  // The dying squad's archetype (for the kill feed icon).
  victimArchetype: 'recon' | 'assault' | 'engineer' | 'shield';
  // Wall-clock at emit.
  atMs: number;
}

export interface FirstBloodEvent {
  killerOwnerId: PlayerId;
  atMs: number;
}

export interface KillStreakEvent {
  // The player whose streak this is (always == viewerId when wired to the
  // viewer's banner, but kept as a field so spectator UIs can read it later).
  killerOwnerId: PlayerId;
  count: number;
  tier: KillStreakTier;
  atMs: number;
}

// 5 s sliding window — matches the audio MULTIKILL_WINDOW_MS in Sfx.ts.
const STREAK_WINDOW_MS = 5000;

function tierFor(count: number): KillStreakTier {
  if (count <= 1) return 'single';
  if (count === 2) return 'double';
  if (count === 3) return 'triple';
  if (count === 4) return 'rampage';
  return 'godlike';
}

type KillHandler = (e: KillEvent) => void;
type FirstBloodHandler = (e: FirstBloodEvent) => void;
type StreakHandler = (e: KillStreakEvent) => void;

export class KillEvents {
  private prev: MatchState | null = null;
  private unsub: Unsubscribe | null = null;
  private viewerId: PlayerId;
  private killHandlers: KillHandler[] = [];
  private firstBloodHandlers: FirstBloodHandler[] = [];
  private streakHandlers: StreakHandler[] = [];
  // Track first-blood per match — reset on `match_ended` via reset().
  private firstBloodFired = false;
  // Viewer-scoped streak window (matches the audio side in Sfx.ts).
  private pendingViewerKillCount = 0;
  private viewerWindowOpenedAt = 0;
  private viewerWindowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(viewerId: PlayerId) {
    this.viewerId = viewerId;
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
    this.resetStreak();
  }

  attach(connection: MatchConnection): void {
    this.detach();
    this.unsub = connection.onSnapshot((snap) => this.onSnapshot(snap));
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
    this.resetStreak();
    this.prev = null;
    this.firstBloodFired = false;
  }

  reset(): void {
    this.detach();
    this.killHandlers.length = 0;
    this.firstBloodHandlers.length = 0;
    this.streakHandlers.length = 0;
  }

  onKill(h: KillHandler): Unsubscribe {
    this.killHandlers.push(h);
    return () => {
      const i = this.killHandlers.indexOf(h);
      if (i !== -1) this.killHandlers.splice(i, 1);
    };
  }

  onFirstBlood(h: FirstBloodHandler): Unsubscribe {
    this.firstBloodHandlers.push(h);
    return () => {
      const i = this.firstBloodHandlers.indexOf(h);
      if (i !== -1) this.firstBloodHandlers.splice(i, 1);
    };
  }

  onStreak(h: StreakHandler): Unsubscribe {
    this.streakHandlers.push(h);
    return () => {
      const i = this.streakHandlers.indexOf(h);
      if (i !== -1) this.streakHandlers.splice(i, 1);
    };
  }

  private onSnapshot(snap: MatchState): void {
    if (this.prev === null) {
      this.prev = snap;
      return;
    }
    this.diffSquads(this.prev, snap);
    this.prev = snap;
  }

  private diffSquads(prev: MatchState, next: MatchState): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    for (const [idStr, before] of Object.entries(prev.squads)) {
      const id = idStr as unknown as EntityId;
      if (next.squads[id] !== undefined) continue;
      // If the last hit came from the neutral camp, don't surface the kill
      // in the feed and don't credit first-blood or streaks — the camp is
      // a PvE hazard, not a player. `null` means neutral; `undefined` can
      // occur in old snapshots that predate this field (show killfeed).
      if (before.lastAttackerPlayerId === null) continue;
      const killerOwnerId: PlayerId = before.ownerId === 0 ? 1 : 0;
      const event: KillEvent = {
        victimOwnerId: before.ownerId,
        killerOwnerId,
        victimSquadId: id,
        victimArchetype: before.archetype,
        atMs: now,
      };
      for (const h of this.killHandlers) h(event);
      // First-blood — fired once per match for the FIRST squad to die.
      if (!this.firstBloodFired) {
        this.firstBloodFired = true;
        for (const h of this.firstBloodHandlers) h({ killerOwnerId, atMs: now });
      }
      // Viewer-scoped streak.
      if (before.ownerId !== this.viewerId) {
        this.registerViewerKill(now);
      }
    }
  }

  private registerViewerKill(now: number): void {
    if (this.pendingViewerKillCount === 0 || now - this.viewerWindowOpenedAt > STREAK_WINDOW_MS) {
      this.pendingViewerKillCount = 0;
      this.viewerWindowOpenedAt = now;
    }
    this.pendingViewerKillCount += 1;
    if (this.viewerWindowTimer !== null) clearTimeout(this.viewerWindowTimer);
    this.viewerWindowTimer = setTimeout(() => this.flushViewerStreak(), STREAK_WINDOW_MS);
  }

  private flushViewerStreak(): void {
    const count = this.pendingViewerKillCount;
    this.pendingViewerKillCount = 0;
    this.viewerWindowOpenedAt = 0;
    this.viewerWindowTimer = null;
    if (count <= 1) return; // single kill — no banner, only the audio kill cue
    const tier = tierFor(count);
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    for (const h of this.streakHandlers) {
      h({ killerOwnerId: this.viewerId, count, tier, atMs: now });
    }
  }

  private resetStreak(): void {
    if (this.viewerWindowTimer !== null) {
      clearTimeout(this.viewerWindowTimer);
      this.viewerWindowTimer = null;
    }
    this.pendingViewerKillCount = 0;
    this.viewerWindowOpenedAt = 0;
  }
}
